import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText, Eye, Loader2, AlertCircle, FileSearch } from 'lucide-react';

import { useApplicationDetails } from './hook/useApplicationDetails';
import {
  DetailsWrapper,
  DetailsContainer,
  HeaderSection,
  HeaderText,
  SectionTitle,
  SectionDescription,
  MetaRow,
  MetaItem,
  ActionButtons,
  DashboardButton,
  Section,
  SectionLabel,
  DocumentsGrid,
  DocumentCard,
  DocumentCardHeader,
  DocumentIconWrapper,
  DocumentTitleGroup,
  DocumentName,
  StatusBadge,
  ConfidenceBadge,
  DocumentReason,
  DocumentCardFooter,
  ViewButton,
  LoadingState,
  EmptyState,
  GlobalError,
} from './ApplicationDetails.styles';
import { useReports } from '../Reports/hook/useReports';

/*
  Route: <Route path="application/:id" element={<ApplicationDetails />} />

  On mount, useApplicationDetails fetches getApplicationStatus(listId).
  If every stage (verification/extraction/analysis/assessment/report)
  is COMPLETE, this renders the documents grid as before.

  If ANY stage isn't complete, this redirects to the flow instead of
  showing the grid — see ApplicationFlow.jsx.
*/

const ApplicationDetails = () => {
  const { id: listId } = useParams();
  const navigate = useNavigate();

  const {
    statusQuery,
    statusInfo,
    allComplete,
    applicationQuery,
    applicationData,
    documentData,
    reportData,
    viewDocumentMutation,
  } = useApplicationDetails(listId);

  const { handleViewReport } = useReports(listId);

  const handleNavToDashboard = () => {
    navigate('/');
  };

  const handleOpenInNewTab = (docUrl) => {
    if (!docUrl) return;
    window.open(docUrl, '_blank', 'noopener,noreferrer');
  };

  const isRedirecting = statusQuery.isSuccess && !allComplete;

  useEffect(() => {
    if (isRedirecting) {
      navigate('/uploadDocuments', {
        replace: true,
        state: { applicationId: listId, statusInfo },
      });
    }
  }, [isRedirecting, listId, statusInfo, navigate]);

  const isInitialLoading =
    statusQuery.isLoading ||
    isRedirecting ||
    (statusQuery.isSuccess && allComplete && applicationQuery.isLoading);
  const hasError = statusQuery.isError || applicationQuery.isError;

  return (
    <DetailsWrapper>
      <DetailsContainer>
        <HeaderSection>
          <HeaderText>
            <SectionTitle>{applicationData?.title || 'Application Details'}</SectionTitle>
            <SectionDescription>
              {applicationData?.description || 'View verified documents for this application'}
            </SectionDescription>
            {applicationData?.createdAt && (
              <MetaRow>
                <MetaItem>
                  Created{' '}
                  {new Date(applicationData.createdAt).toLocaleDateString('en-AU', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </MetaItem>
              </MetaRow>
            )}
          </HeaderText>

          <ActionButtons>
            <DashboardButton type="button" onClick={handleNavToDashboard}>
              Dashboard
            </DashboardButton>
          </ActionButtons>
        </HeaderSection>

        {hasError && (
          <GlobalError>
            <AlertCircle size={16} />
            {statusQuery.error?.message ||
              applicationQuery.error?.message ||
              'Failed to load application details.'}
          </GlobalError>
        )}

        <Section>
          <SectionLabel>Documents</SectionLabel>

          {isInitialLoading && (
            <LoadingState>
              <Loader2 size={22} className="spin" />
              <span>
                {isRedirecting ? 'Resuming your application…' : 'Loading application details…'}
              </span>
            </LoadingState>
          )}

          {!isInitialLoading && !hasError && documentData.length === 0 && (
            <EmptyState>
              <FileSearch size={36} strokeWidth={1.2} />
              <p style={{ margin: 0 }}>No documents found for this application.</p>
            </EmptyState>
          )}

          {!isInitialLoading && documentData.length > 0 && (
            <DocumentsGrid>
              {documentData.map((doc) => (
                <DocumentCard key={doc._id}>
                  <DocumentCardHeader>
                    <DocumentIconWrapper>
                      <FileText />
                    </DocumentIconWrapper>
                    <DocumentTitleGroup>
                      <DocumentName>{doc.name}</DocumentName>
                      <StatusBadge $status={doc.verificationStatus}>
                        {doc.verificationStatus}
                      </StatusBadge>
                    </DocumentTitleGroup>
                    {typeof doc.verificationResponse?.confidenceScore === 'number' && (
                      <ConfidenceBadge $score={doc.verificationResponse.confidenceScore}>
                        {doc.verificationResponse.confidenceScore}%
                      </ConfidenceBadge>
                    )}
                  </DocumentCardHeader>

                  {doc.verificationResponse?.reason && (
                    <DocumentReason>{doc.verificationResponse.reason}</DocumentReason>
                  )}

                  <DocumentCardFooter>
                    <ViewButton type="button" onClick={() => handleOpenInNewTab(doc.docUrl)}>
                      <Eye size={14} /> View
                    </ViewButton>
                  </DocumentCardFooter>
                </DocumentCard>
              ))}
            </DocumentsGrid>
          )}
        </Section>

        <Section style={{ marginTop: '1.5rem' }}>
          <SectionLabel>Reports</SectionLabel>

          {isInitialLoading && (
            <LoadingState>
              <Loader2 size={22} className="spin" />
              <span>
                {isRedirecting ? 'Resuming your application…' : 'Loading application details…'}
              </span>
            </LoadingState>
          )}

          {!isInitialLoading && !hasError && reportData.length === 0 && (
            <EmptyState>
              <FileSearch size={36} strokeWidth={1.2} />
              <p style={{ margin: 0 }}>No reports found for this application.</p>
            </EmptyState>
          )}

          {!isInitialLoading && !hasError && reportData.length > 0 && (
            <DocumentsGrid>
              {reportData.map((report) => {
                const isViewing =
                  viewDocumentMutation.isPending &&
                  viewDocumentMutation.variables?.documentId === report._id;

                return (
                  <DocumentCard key={report._id}>
                    <DocumentCardHeader>
                      <DocumentIconWrapper>
                        <FileText />
                      </DocumentIconWrapper>
                      <DocumentTitleGroup>
                        <DocumentName>{report.name}</DocumentName>
                      </DocumentTitleGroup>
                    </DocumentCardHeader>

                    <DocumentCardFooter>
                      <ViewButton
                        type="button"
                        onClick={() => handleViewReport(report._id)}
                        disabled={isViewing}
                      >
                        {isViewing ? (
                          <>
                            <Loader2 size={14} className="spin" /> Opening…
                          </>
                        ) : (
                          <>
                            <Eye size={14} /> View
                          </>
                        )}
                      </ViewButton>
                    </DocumentCardFooter>
                  </DocumentCard>
                );
              })}
            </DocumentsGrid>
          )}
        </Section>
      </DetailsContainer>
    </DetailsWrapper>
  );
};

export default ApplicationDetails;
